import type {
  PaginatedExtendedGroupByQuery,
  ProjectChartDatapoint,
  TimeSeriesQuery,
} from '@/api/generated/model';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';
import { levelSharePercentsFromCounts } from '@/domains/pages/ai-proficiency/utils/ladder';
import { normalizeTrendScore } from '@/domains/pages/shared/chart-axis';

export type ScorePercentChange = number | 'na' | null;

export type OrgScoreSnapshot = {
  score: number | null;
  scorePrev: number | null;
  delta: ScorePercentChange;
  totalUsers: number;
};

export type LevelTeamBreakdown = {
  team: string;
  count: number;
};

export type LevelSnapshot = {
  level: AifiLevel;
  userCount: number;
  sharePercent: number;
  teams: LevelTeamBreakdown[];
};

const LEVEL_SET = new Set<string>(AIFI_LEVELS);

export function computeScorePercentChange(
  score: number | null,
  scorePrev: number | null,
): ScorePercentChange {
  if (score == null || scorePrev == null) return null;
  if (scorePrev === 0) return score === 0 ? 0 : 'na';

  return ((score - scorePrev) / scorePrev) * 100;
}

export function mapOrgScoreSnapshot(data: PaginatedExtendedGroupByQuery): OrgScoreSnapshot {
  const row = data.data[0];
  const score = row?.aifi_score ?? null;
  const scorePrev = row?.aifi_score_prev ?? null;
  const totalUsers = data.total_users ?? row?.n_users ?? row?.total_users ?? 0;
  const delta = computeScorePercentChange(score, scorePrev);

  return {
    score,
    scorePrev,
    delta,
    totalUsers,
  };
}

export function mergeLevelSnapshotsWithTeams(
  proficiencyLevels: LevelSnapshot[],
  teamsData: PaginatedExtendedGroupByQuery | undefined,
): LevelSnapshot[] {
  if (!teamsData) return proficiencyLevels;

  const teamsByLevel = new Map(mapLevelSnapshots(teamsData).map((level) => [level.level, level.teams]));

  return proficiencyLevels.map((level) => ({
    ...level,
    teams: teamsByLevel.get(level.level) ?? [],
  }));
}

export function mapLevelSnapshots(data: PaginatedExtendedGroupByQuery): LevelSnapshot[] {
  const byLevel = new Map<AifiLevel, { count: number; teams: Map<string, number> }>();

  for (const row of data.data) {
    const levelName = row.group_name;
    if (!LEVEL_SET.has(levelName)) continue;

    const level = levelName as AifiLevel;
    const count = row.n_users ?? 0;
    const team = row.group_name_2?.trim();

    if (!byLevel.has(level)) {
      byLevel.set(level, { count: 0, teams: new Map() });
    }

    const entry = byLevel.get(level)!;
    entry.count += count;

    if (team && count > 0) {
      entry.teams.set(team, (entry.teams.get(team) ?? 0) + count);
    }
  }

  const sharePercents = levelSharePercentsFromCounts((level) => byLevel.get(level)?.count ?? 0);

  return AIFI_LEVELS.map((level) => {
    const entry = byLevel.get(level);
    const userCount = entry?.count ?? 0;
    const sharePercent = sharePercents[level];
    const teams = Array.from(entry?.teams.entries() ?? [])
      .map(([team, teamCount]) => ({ team, count: teamCount }))
      .filter((teamRow) => teamRow.count > 0)
      .sort((a, b) => b.count - a.count);

    return {
      level,
      userCount,
      sharePercent,
      teams,
    };
  });
}

export function mapLevelMixSeries(
  buckets: string[],
  datasets: Record<string, TimeSeriesQuery[] | undefined>,
): Record<AifiLevel, number[]> {
  const result: Record<AifiLevel, number[]> = {
    Expert: [],
    Practitioner: [],
    Beginner: [],
    Novice: [],
  };

  const flatRows = Object.values(datasets)
    .flat()
    .filter((row): row is TimeSeriesQuery => row != null);

  for (const bucket of buckets) {
    const bucketRows = datasets[bucket]?.filter((row): row is TimeSeriesQuery => row != null);
    const rows: TimeSeriesQuery[] = bucketRows
      ? bucketRows
      : flatRows.filter((row) => row.bucket === bucket && LEVEL_SET.has(row.group_name ?? ''));

    const sharePercents = levelSharePercentsFromCounts((level) => {
      const levelRows = rows.filter((row) => row.group_name === level);
      return levelRows.reduce((sum, row) => sum + (row.n_users ?? 0), 0);
    });

    for (const level of AIFI_LEVELS) {
      result[level].push(sharePercents[level]);
    }
  }

  return result;
}

function resolveChartDatasetLabel(label: string): AifiLevel | null {
  const normalized = label.trim();
  if (LEVEL_SET.has(normalized)) return normalized as AifiLevel;

  const match = AIFI_LEVELS.find(
    (level) => level.toLowerCase() === normalized.toLowerCase(),
  );
  return match ?? null;
}

export function mapOrgScoreTimeFromChartData(chart: ProjectChartDatapoint): {
  buckets: string[];
  scores: Array<number | null>;
} {
  const buckets = chart.x_label_values ?? [];
  const primaryDataset = chart.datasets?.[0];
  const scores = primaryDataset?.data.map((point) => normalizeTrendScore(point.y)) ?? [];

  return { buckets, scores };
}

export function mapLevelMixFromChartData(chart: ProjectChartDatapoint): {
  buckets: string[];
  mix: Record<AifiLevel, number[]>;
  counts: Record<AifiLevel, number[]>;
} {
  const buckets = chart.x_label_values ?? [];
  const mix: Record<AifiLevel, number[]> = {
    Expert: [],
    Practitioner: [],
    Beginner: [],
    Novice: [],
  };
  const counts: Record<AifiLevel, number[]> = {
    Expert: [],
    Practitioner: [],
    Beginner: [],
    Novice: [],
  };

  const datasetsByLevel = new Map<AifiLevel, ProjectChartDatapoint['datasets'][number]>();
  for (const dataset of chart.datasets ?? []) {
    const level = resolveChartDatasetLabel(dataset.label);
    if (level) datasetsByLevel.set(level, dataset);
  }

  for (let index = 0; index < buckets.length; index++) {
    const bucketCounts: Record<AifiLevel, number> = {
      Expert: 0,
      Practitioner: 0,
      Beginner: 0,
      Novice: 0,
    };

    for (const level of AIFI_LEVELS) {
      bucketCounts[level] = datasetsByLevel.get(level)?.data[index]?.y ?? 0;
    }

    const sharePercents = levelSharePercentsFromCounts((level) => bucketCounts[level]);

    for (const level of AIFI_LEVELS) {
      mix[level].push(sharePercents[level]);
      counts[level].push(bucketCounts[level]);
    }
  }

  return { buckets, mix, counts };
}

export function mapOrgScoreTimeSeries(
  buckets: string[],
  datasets: Record<string, TimeSeriesQuery[] | undefined>,
): number[] {
  const flatRows = Object.values(datasets)
    .flat()
    .filter((entry): entry is TimeSeriesQuery => entry != null);

  return buckets.map((bucket) => {
    const bucketRows = datasets[bucket];
    const firstRow = bucketRows?.[0];
    if (firstRow?.aifi_score != null) return firstRow.aifi_score;

    const row = flatRows.find((entry) => entry.bucket === bucket && !entry.group_name);
    if (row?.aifi_score != null) return row.aifi_score;

    const fallback = flatRows.find((entry) => entry.bucket === bucket);
    return fallback?.aifi_score ?? 0;
  });
}
